import { DateTime } from "luxon";
import { DeviceLite } from "../device/device.model";
import { PointGroup } from "../pointgroup/point-group.model";
import { AlarmValue } from "../alarm/alarm.model";

export enum PointType {
  None = 0,
  DI = 1, // Indication
  DO = 2,
  AI = 3, // Measurement
  AO = 4,
  ConversionTable = 5,
  TimeSchedule = 6,
  Alarm = 7,
  Control = 13,
  Calendar = 17,
}

export enum LockState {
  Auto = 0,
  Program = 1,
  Manual = 2,
}

export enum ModeType {
  Manual = "Manual",
  Program = "Program",
  Auto = "Auto",
}
export type AILimitExtension = {
  value: number | null;
  lockState: number;
  name: string;
  index: number;
};
export type ControlType = Record<number, number>;

export type PuckExtension = {
  limits?: AILimitExtension[];
  control?: ControlType;

  state?: {
    value: number;
    lockState: LockState;
  };
};

export type PointGroupLite = Pick<PointGroup, "id" | "name">;

export type PuckValue = {
  timestamp: string;
  deviceId: string;
  pointName: string;
  value: number | null;
  label: string | null;
  lockState: LockState | null;
  alarmState: number | null;
  alarmGroup: string | null;
  isAlarmActive?: boolean;
  errorState: number | null;
  deviceState: number | null;
  unit: string;
  decimals: number | null;
  extension?: PuckExtension;
  lastUpdate?: string;
  dataType: string;
};

export class PointValue {
  timestamp: DateTime;
  lastUpdate?: string;
  value: number | null;
  lockState: LockState | null;
  errorState: number | null;
  label?: string;
  extension?: PuckExtension;
  deviceState: number | null;
  dataType: string | null;

  constructor(value: PuckValue) {
    this.timestamp = DateTime.fromISO(value.timestamp);
    this.lastUpdate = value.lastUpdate;
    this.value = value.value;
    this.lockState = value.lockState;
    this.errorState = value.errorState;
    this.label = value.label ? value.label : undefined;
    this.extension = value.extension;
    this.deviceState = value.deviceState;
    this.dataType = value.dataType;
  }
}

export type PointResponse = Omit<
  Point,
  "value" | "userModifiedAt" | "updatedAt"
> & {
  value?: PuckValue;
  userModifiedAt: string | null;
  updatedAt: string;
};

type PointPendingInfo = Pick<PointWithValue, "updatedAt" | "userModifiedAt"> & {
  value?: PointValue | AlarmValue;
};
export function getIsPointPending({
  updatedAt,
  userModifiedAt,
  value,
}: PointPendingInfo): boolean {
  const userModifiedAtInMillis = userModifiedAt
    ? userModifiedAt.toMillis()
    : null;

  const valueTimestampInMillis = value?.timestamp
    ? value.timestamp.toMillis()
    : null;

  const pointUpdatedTimestampInMillis = updatedAt.toMillis();

  if (!userModifiedAtInMillis) {
    return false;
  } else {
    const isValuePending = !valueTimestampInMillis
      ? undefined
      : userModifiedAtInMillis >= valueTimestampInMillis;

    const isPointPending =
      userModifiedAtInMillis >= pointUpdatedTimestampInMillis;

    return isValuePending !== undefined
      ? isValuePending && isPointPending
      : isPointPending;
  }
}
export abstract class PointBase {
  id: number;
  name: string;
  description: string;
  type: PointType;
  decimals: number | null;
  unit: string | null;
  userModifiedAt: DateTime | null;
  updatedAt: DateTime;
  isPending: boolean;

  constructor(point: PointResponse) {
    this.id = point.id;
    this.name = point.name;
    this.description = point.description;
    this.type = point.type;
    this.decimals = point.decimals;
    this.unit = point.unit;
    this.userModifiedAt = point.userModifiedAt
      ? DateTime.fromISO(point.userModifiedAt)
      : null;
    this.updatedAt = DateTime.fromISO(point.updatedAt);
    this.isPending = getIsPointPending(this);
  }
}
export class PointWithValue extends PointBase {
  value?: PointValue;

  constructor(point: PointResponse) {
    super(point);
    this.value =
      point.value && Object.keys(point.value).length > 0
        ? new PointValue(point.value)
        : undefined;
    this.isPending = getIsPointPending(this);
  }
}

type PointExtension = {
  units: Record<string, string> | undefined;
};

export class Point extends PointWithValue {
  device: DeviceLite;
  buildingName: string | null;
  stateTexts?: { label: string; value: number }[];
  icalWeek?: string;
  icalExceptions?: string;
  graphicLink?: string | null;
  ical?: string;
  extension?: PointExtension;
  pointGroups: PointGroupLite[];

  constructor(point: PointResponse) {
    super(point);
    this.device = point.device;
    this.buildingName = point.buildingName || null;
    this.stateTexts = point.stateTexts;
    this.icalWeek = point.icalWeek;
    this.icalExceptions = point.icalExceptions;
    this.graphicLink = point.graphicLink;
    this.ical = point.ical;
    this.extension = point.extension;
    this.pointGroups = point.pointGroups;
  }
}

export type PointSetValue = {
  id: number;
  name?: string;
  type: PointType;
  value?: {
    value?: number | null;
    lockState?: LockState | null;
    extension?: {
      limits?: {
        value: number | null;
        lockState: LockState;
        index: number;
      }[];
    };
  };
};

export interface PointWithBuilding {
  buildingName: string | null;
  buildingId?: number | null;
}

export interface WithValue {
  value?: PointValue;
}
export class PointHistoryItem {
  id: number;
  timestamp: DateTime;
  value: number | null;
  deviceState: number | null;
  unit: string | null;
  type: PointType;
  tooltipLabel?: string;
  limits?: AILimitExtension[];
  lockState?: LockState | null;

  constructor(pointValue: PuckValue, point: Point) {
    this.id = point.id;
    this.timestamp = DateTime.fromISO(pointValue.timestamp);
    this.value = pointValue.value;
    this.deviceState = pointValue.deviceState;
    this.unit = point.unit;
    this.type = point.type;
    this.tooltipLabel = pointValue.label ? pointValue.label : undefined;
    this.limits = pointValue.extension?.limits;
    this.lockState = pointValue.lockState;
  }
}

export function getModelName(type: PointType) {
  const typeMap: { [key: number]: string } = {
    [PointType.Alarm]: "alarms",
    [PointType.DI]: "indications",
    [PointType.AI]: "measurements",
    [PointType.AO]: "ao",
    [PointType.DO]: "do",
    [PointType.Control]: "controls",
    [PointType.TimeSchedule]: "time-schedule",
  };

  if (!(type in typeMap)) {
    throw new Error("Invalid point type");
  }
  return typeMap[type];
}

export type MargaretPoint = {
  unit: string;
  timestamp: string;
  deviceId: string;
  pointName: string;
  value: number | null;
  decimals: number | null;
  state: string | null;
  lockState: number | null;
  alarmState: number | null;
  errorState: number | null;
  deviceState: number | null;
  extension: PuckExtension | null;
  description?: string | null;
};
